
import { ref, useContext, computed, watch } from '@nuxtjs/composition-api';
import { SIM_CARD_TYPE } from '~/constants/CheckoutFlow';
import useTlmCart from '~/composable/useTlmCart';

const ESIM_DESCRIPTION = 'eSIM er et digitalt SIM-kort, som downloades i vores selvbetjening Mit Telmore. Det er nemt og hurtigt at komme i gang. Vi sender også en mail med en guide, så du kommer godt i gang.'
const PHYSICAL_SIM_DESCRIPTION = 'Vi sender et fysisk SIM-kort, du modtager med posten indenfor 5 hverdage.'

const getSimItems = (isNumberPorting) => {
  const esimLabel = isNumberPorting ? 'Anbefalet valg' : 'I gang på 5-10 min.'
  const physicalLabel = isNumberPorting ? '' : 'I gang på 3-5 dage'

  return {
    esim: {
      name: 'eSIM',
      description: ESIM_DESCRIPTION,
      label: esimLabel
    },
    physical: {
      name: 'Fysisk SIM-kort',
      description: PHYSICAL_SIM_DESCRIPTION,
      label: physicalLabel
    }
  }
}

const resolveSelectedValue = (simType) => {
  if (simType === SIM_CARD_TYPE.PHYSICAL_SIM) {
    return SIM_CARD_TYPE.PHYSICAL_SIM
  }
  return SIM_CARD_TYPE.E_SIM
}

export default {
  name: 'TlmCheckoutSimTypeRadios',
  props: {
    defaultValue: {
      type: String,
      default: ''
    },
    isSignupWithDevice: { // new number
      type: Boolean,
      default: false
    },
    isSimOnly: { // sim only
      type: Boolean,
      default: false
    },
    isNpWithoutDevice: { // sim only
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    isNumberPorting: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { app } = useContext()
    const { hasAnyEsim, isMobiltelefonerCategory, cartHasSubscription } = useTlmCart(app)
    const selected = ref(null)

    const simItems = computed(() => getSimItems(props.isNumberPorting))
    const esimItem = computed(() => simItems.value.esim)
    const physicalItem = computed(() => simItems.value.physical)

    const onChange = (value) => {
      emit('input', value)
    }

    const preselectSim = () => {
      const value = props.defaultValue
        ? resolveSelectedValue(props.defaultValue)
        : SIM_CARD_TYPE.E_SIM
      selected.value = value
      emit('input', value)
    }

    /**
     * Decide whether the SIM selector UI should be shown.
     *
     * Rules:
     * - Mobiltelefoner → only show if SIM-only or device supports eSIM (E-SIM = true).
     * - Other categories → show only if SIM-only or cart includes a subscription.
     */
    const showSimSelector = computed(() => {
      const hasSubscriptionFlow = props.isSimOnly || cartHasSubscription.value
      if (isMobiltelefonerCategory.value) {
        return props.isSimOnly || hasAnyEsim()
      }
      return hasSubscriptionFlow
    })

    /**
     * Watch the visibility condition:
     * - If the selector should NOT be shown, we force a default
     *   (physical SIM) and reset the local selection state.
     * - If shown, pre-select eSIM unless a default value is provided.
     * - Runs immediately on mount, so the parent always
     *   receives a consistent default value.
     */
    watch(
      showSimSelector,
      (isVisible) => {
        if (!isVisible) {
          emit('input', SIM_CARD_TYPE.PHYSICAL_SIM) // fallback default
          selected.value = null // clear UI selection
        } else {
          preselectSim()
        }
      },
      { immediate: true }
    )

    watch(
      () => props.defaultValue,
      (value) => {
        if (showSimSelector.value && value) {
          preselectSim()
        }
      }
    )

    return {
      showSimSelector,
      hasAnyEsim,
      selected,
      esimItem,
      physicalItem,
      SIM_CARD_TYPE,
      onChange
    }
  }
}
